import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Building2, Eye, EyeOff, AlertCircle, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Alert, AlertDescription } from '../components/ui/alert';

const Register = () => {
  const navigate = useNavigate();
  const { signup, user, loading: authLoading } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    orgName: ''
  });

  useEffect(() => {
    if (user && !authLoading) {
      navigate('/app/matters');
    }
  }, [user, authLoading, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) {
      setError('');
    }
  };

  const validateForm = () => {
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return false;
    }
    if (!formData.orgName.trim()) {
      setError('Organization name is required');
      return false;
    }
    if (!formData.email) {
      setError('Email is required');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/\d/.test(formData.password)) {
      setError('Password must contain at least one number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const success = await signup(formData.email, formData.password, formData.orgName, formData.fullName);
      if (success) {
        navigate('/app/matters');
      } else {
        setError('Signup failed. Please check your information and try again.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'An error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#fefdfb' }}>
        <div style={{ color: '#64748b', fontFamily: 'Inter, sans-serif' }}>Loading...</div>
      </div>
    );
  }

  const inputStyle = {
    width: '100%',
    height: '48px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    paddingLeft: '44px',
    paddingRight: '16px',
    fontSize: '15px',
    fontFamily: 'Inter, sans-serif',
    color: '#1e293b',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    backgroundColor: '#ffffff',
  };

  const inputFocusHandler = (e) => {
    e.target.style.borderColor = '#f59e0b';
    e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.12)';
  };

  const inputBlurHandler = (e) => {
    e.target.style.borderColor = '#e2e8f0';
    e.target.style.boxShadow = 'none';
  };

  return (
    <div className="register-container" style={{ display: 'flex', minHeight: '100vh', flexDirection: 'row' }}>
      <style>{`
        @media (max-width: 768px) {
          .register-container { flex-direction: column !important; }
          .register-left { width: 100% !important; min-height: auto !important; padding: 32px 24px !important; }
          .register-right { width: 100% !important; padding: 32px 24px !important; }
        }
      `}</style>

      <div
        className="register-left"
        style={{
          width: '45%',
          backgroundColor: '#1e293b',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '60px 56px',
          minHeight: '100vh',
        }}
      >
        <div style={{ maxWidth: '420px' }}>
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '28px',
              fontWeight: 400,
              color: '#ffffff',
              marginBottom: '48px',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            <span style={{ textDecoration: 'underline', textDecorationColor: '#B35309', textUnderlineOffset: '4px' }}>b</span>arrow.
          </div>

          <h1 style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '32px',
            fontWeight: 400,
            color: '#ffffff',
            lineHeight: 1.3,
            marginBottom: '20px',
          }}>
            Document review that shows its work.
          </h1>

          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '16px',
            fontWeight: 400,
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            maxWidth: '380px',
            marginBottom: '40px',
          }}>
            Upload contracts, filings, or discovery docs. Get structured summaries with citations you can verify in seconds—not hours.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '40px' }}>
            {[
              '3 documents free to start',
              'No credit card required',
              'Documents auto-delete in 24 hours',
            ].map((text, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(245, 158, 11, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Check style={{ width: '14px', height: '14px', color: '#f59e0b', strokeWidth: 2.5 }} />
                </div>
                <span style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#ffffff',
                }}>
                  {text}
                </span>
              </div>
            ))}
          </div>

          <div style={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            paddingTop: '28px',
          }}>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '15px',
              fontStyle: 'italic',
              color: 'rgba(255, 255, 255, 0.85)',
              lineHeight: 1.6,
              marginBottom: '16px',
            }}>
              "Cut my contract review prep time in half. And I can actually show clients where each finding came from."
            </p>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: '#f59e0b',
              marginBottom: '4px',
            }}>
              — Rachel T.
            </p>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              color: 'rgba(255, 255, 255, 0.5)',
            }}>
              Solo practitioner, business law
            </p>
          </div>
        </div>
      </div>

      <div
        className="register-right"
        style={{
          width: '55%',
          backgroundColor: '#fefdfb',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 56px',
          minHeight: '100vh',
        }}
      >
        <div style={{ width: '100%', maxWidth: '440px' }}>
          <h2 style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '26px',
            fontWeight: 400,
            color: '#1e293b',
            marginBottom: '8px',
          }}>
            Create your account
          </h2>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '15px',
            color: '#64748b',
            marginBottom: '32px',
          }}>
            Start reviewing documents in minutes. No setup complexity.
          </p>

          {error && (
            <Alert className="mb-4 bg-red-50 border-red-200" ref={errorRef}>
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800" data-testid="error-message">
                {error}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 500, color: '#1e293b', marginBottom: '6px' }}>
                Full Name
              </label>
              <div style={{ position: 'relative' }}>
                <User style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#94a3b8' }} />
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="John Smith"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  onFocus={inputFocusHandler}
                  onBlur={inputBlurHandler}
                  style={inputStyle}
                  data-testid="fullname-input"
                />
              </div>
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 500, color: '#1e293b', marginBottom: '6px' }}>
                Firm or Organization
              </label>
              <div style={{ position: 'relative' }}>
                <Building2 style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#94a3b8' }} />
                <input
                  id="orgName"
                  name="orgName"
                  type="text"
                  placeholder="Smith & Associates LLP"
                  value={formData.orgName}
                  onChange={handleInputChange}
                  onFocus={inputFocusHandler}
                  onBlur={inputBlurHandler}
                  style={inputStyle}
                  data-testid="orgname-input"
                />
              </div>
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 500, color: '#1e293b', marginBottom: '6px' }}>
                Email
              </label>
              <div style={{ position: 'relative' }}>
                <Mail style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#94a3b8' }} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@smithlaw.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  onFocus={inputFocusHandler}
                  onBlur={inputBlurHandler}
                  style={inputStyle}
                  data-testid="email-input"
                />
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 500, color: '#1e293b', marginBottom: '6px' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#94a3b8' }} />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  onFocus={inputFocusHandler}
                  onBlur={inputBlurHandler}
                  style={{ ...inputStyle, paddingRight: '44px' }}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {showPassword
                    ? <EyeOff style={{ width: '18px', height: '18px', color: '#94a3b8' }} />
                    : <Eye style={{ width: '18px', height: '18px', color: '#94a3b8' }} />
                  }
                </button>
              </div>
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                color: '#94a3b8',
                marginTop: '6px',
              }}>
                Min 8 characters with uppercase, lowercase, and number
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: '52px',
                backgroundColor: loading ? '#334155' : '#1e293b',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                fontFamily: 'Inter, sans-serif',
                fontSize: '15px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => { if (!loading) e.target.style.backgroundColor = '#334155'; }}
              onMouseLeave={(e) => { if (!loading) e.target.style.backgroundColor = '#1e293b'; }}
              data-testid="submit-button"
            >
              {loading ? 'Please wait...' : 'Create Account'}
            </button>
          </form>

          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '13px',
            color: '#64748b',
            textAlign: 'center',
            marginTop: '24px',
            lineHeight: 1.5,
          }}>
            By continuing, you agree to our{' '}
            <a href="https://barrow.legal/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: '#d97706', textDecoration: 'none' }}>Terms of Service</a>
            {' '}and{' '}
            <a href="https://barrow.legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#d97706', textDecoration: 'none' }}>Privacy Policy</a>.
          </p>

          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            color: '#94a3b8',
            textAlign: 'center',
            marginTop: '8px',
          }}>
            Barrow is not a law firm and does not provide legal advice.
          </p>

          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            textAlign: 'center',
            marginTop: '24px',
            color: '#64748b',
          }}>
            Already have an account?{' '}
            <Link to="/auth" style={{ color: '#d97706', fontWeight: 500, textDecoration: 'none' }}>
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
